// Art History Brush Tool — painterly brush strokes from a history state.
// Creates stylized, impressionistic effects by sampling from a history state
// and applying brush strokes with style variations.

import { PaintStrokeCommand, snapshotRegion } from "../commands/PaintStrokeCommand";
import { screenToDoc } from "../render/Viewport";
import type { RasterLayer } from "../document/Layer";
import { findRaster } from "../document/LayerUtils";
import { mergeBBox, type BBox } from "../pixel/brush";
import type { PointerSample, Tool, ToolContext } from "./Tool";

export type ArtStyle = "tight-short" | "tight-medium" | "tight-long" | "loose-medium" | "loose-long" | "dab" | "tight-curl" | "loose-curl";

export interface ArtHistoryBrushSettings {
  size: number;
  opacity: number;
  style: ArtStyle;
  area: number; // brush coverage area
  tolerance: number; // color tolerance (0=unrestricted)
}

export class ArtHistoryBrushTool implements Tool {
  readonly id = "art-history-brush";
  readonly shortcut = "Y";

  settings: ArtHistoryBrushSettings = {
    size: 30,
    opacity: 0.5,
    style: "loose-medium",
    area: 100,
    tolerance: 0,
  };

  /** Source pixels from a history state. */
  sourcePixels: Uint8ClampedArray | null = null;
  sourceWidth = 0;
  sourceHeight = 0;

  private active: {
    layer: RasterLayer;
    layerSnapshot: Uint8ClampedArray;
    lastX: number;
    lastY: number;
    bbox: BBox | null;
    strokeCount: number;
  } | null = null;

  onPointerDown(ctx: ToolContext, s: PointerSample): void {
    if (!ctx.doc || !ctx.doc.activeLayerId) return;
    if (!this.sourcePixels) return;

    const layer = findRaster(ctx.doc.layers, ctx.doc.activeLayerId);
    if (!layer || !layer.visible) return;
    if (layer.lockAll || layer.lockImage) {
      ctx.showTip("Layer is locked.");
      return;
    }

    const p = screenToDoc(ctx.viewport, s.x, s.y);
    const docX = p.x - layer.x;
    const docY = p.y - layer.y;

    this.active = {
      layer,
      layerSnapshot: new Uint8ClampedArray(layer.pixels.data),
      lastX: docX,
      lastY: docY,
      bbox: null,
      strokeCount: 0,
    };

    this.paintStroke(ctx, docX, docY, s);
    ctx.requestRender();
  }

  onPointerMove(ctx: ToolContext, s: PointerSample): void {
    if (!this.active) return;
    const p = screenToDoc(ctx.viewport, s.x, s.y);
    const docX = p.x - this.active.layer.x;
    const docY = p.y - this.active.layer.y;

    // Space stamps based on style
    const spacing = this.getSpacing();
    const dx = docX - this.active.lastX;
    const dy = docY - this.active.lastY;
    const len = Math.hypot(dx, dy);
    const n = Math.max(1, Math.ceil(len / spacing));

    for (let i = 1; i <= n; i++) {
      const t = i / n;
      const cx = this.active.lastX + dx * t;
      const cy = this.active.lastY + dy * t;
      this.paintStroke(ctx, cx, cy, s);
    }

    this.active.lastX = docX;
    this.active.lastY = docY;
    ctx.requestRender();
  }

  onPointerUp(ctx: ToolContext): void {
    if (!this.active) return;
    if (!this.active.bbox) { this.active = null; return; }

    const { layer, layerSnapshot, bbox } = this.active;
    const after = snapshotRegion(layer, bbox);
    const before = this.extractRegion(layerSnapshot, layer.pixels.width, bbox);
    this.active = null;
    ctx.emit(new PaintStrokeCommand(layer.id, bbox, before, after));
  }

  private getSpacing(): number {
    switch (this.settings.style) {
      case "tight-short": return Math.max(1, this.settings.size / 8);
      case "tight-medium": return Math.max(1, this.settings.size / 6);
      case "tight-long": return Math.max(1, this.settings.size / 4);
      case "loose-medium": return Math.max(1, this.settings.size / 3);
      case "loose-long": return Math.max(1, this.settings.size / 2);
      case "dab": return Math.max(1, this.settings.size);
      case "tight-curl": return Math.max(1, this.settings.size / 5);
      case "loose-curl": return Math.max(1, this.settings.size / 2.5);
      default: return Math.max(1, this.settings.size / 4);
    }
  }

  private paintStroke(ctx: ToolContext, docX: number, docY: number, _s: PointerSample): void {
    if (!this.active || !this.sourcePixels) return;
    const layer = this.active.layer;
    const dstData = layer.pixels.data;
    const srcData = this.sourcePixels;
    const sw = this.sourceWidth;
    const sh = this.sourceHeight;
    const dw = layer.pixels.width;
    const dh = layer.pixels.height;
    const radius = this.settings.size / 2;
    const opacity = this.settings.opacity;

    // Selection mask
    const sel = (ctx.doc as any)?.selection as { mask?: Uint8Array } | null;
    const selMask = sel?.mask;

    const minX = Math.max(0, Math.floor(docX - radius));
    const minY = Math.max(0, Math.floor(docY - radius));
    const maxX = Math.min(dw - 1, Math.ceil(docX + radius));
    const maxY = Math.min(dh - 1, Math.ceil(docY + radius));

    let touchX0 = dw, touchY0 = dh, touchX1 = -1, touchY1 = -1;

    // Style-based brush shape
    const styleOffset = this.getStyleOffset();

    for (let py = minY; py <= maxY; py++) {
      const dy = py + 0.5 - docY;
      for (let px = minX; px <= maxX; px++) {
        const dx = px + 0.5 - docX;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist >= radius) continue;

        // Selection masking
        if (selMask && selMask[py * dw + px] === 0) continue;

        // Lock transparency
        if (layer.lockTransparency) {
          const dstIdx = (py * dw + px) * 4;
          if (dstData[dstIdx + 3] === 0) continue;
        }

        // Tolerance: skip if source color is too different from current
        if (this.settings.tolerance > 0) {
          const sx = Math.round(px + (sw - dw) / 2);
          const sy = Math.round(py + (sh - dh) / 2);
          if (sx < 0 || sx >= sw || sy < 0 || sy >= sh) continue;
          const srcIdx = (sy * sw + sx) * 4;
          const dstIdx = (py * dw + px) * 4;
          const dr = dstData[dstIdx]! - srcData[srcIdx]!;
          const dg = dstData[dstIdx + 1]! - srcData[srcIdx + 1]!;
          const db = dstData[dstIdx + 2]! - srcData[srcIdx + 2]!;
          if (Math.sqrt(dr * dr + dg * dg + db * db) > this.settings.tolerance) continue;
        }

        // Map to source position
        const sx = Math.round(px + (sw - dw) / 2 + styleOffset.x);
        const sy = Math.round(py + (sh - dh) / 2 + styleOffset.y);
        if (sx < 0 || sx >= sw || sy < 0 || sy >= sh) continue;

        const dstIdx = (py * dw + px) * 4;
        const srcIdx = (sy * sw + sx) * 4;

        // Soft-edge falloff with style variation
        const falloff = this.getStyleFalloff(dist / radius);
        const blend = falloff * opacity;

        dstData[dstIdx] = Math.round(dstData[dstIdx]! * (1 - blend) + srcData[srcIdx]! * blend);
        dstData[dstIdx + 1] = Math.round(dstData[dstIdx + 1]! * (1 - blend) + srcData[srcIdx + 1]! * blend);
        dstData[dstIdx + 2] = Math.round(dstData[dstIdx + 2]! * (1 - blend) + srcData[srcIdx + 2]! * blend);
        dstData[dstIdx + 3] = Math.round(dstData[dstIdx + 3]! * (1 - blend) + srcData[srcIdx + 3]! * blend);

        if (px < touchX0) touchX0 = px;
        if (py < touchY0) touchY0 = py;
        if (px > touchX1) touchX1 = px;
        if (py > touchY1) touchY1 = py;
      }
    }

    if (touchX1 >= 0) {
      const bb: BBox = { x: touchX0, y: touchY0, w: touchX1 - touchX0 + 1, h: touchY1 - touchY0 + 1 };
      this.active.bbox = this.active.bbox ? mergeBBox(this.active.bbox, bb) : bb;
    }
    this.active.strokeCount++;
    layer.pixels.markDirty();
  }

  private getStyleOffset(): { x: number; y: number } {
    const count = this.active?.strokeCount ?? 0;
    switch (this.settings.style) {
      case "tight-curl":
      case "loose-curl": {
        const angle = count * 0.5;
        const amp = this.settings.style === "tight-curl" ? 2 : 5;
        return { x: Math.cos(angle) * amp, y: Math.sin(angle) * amp };
      }
      case "dab":
        return { x: (Math.random() - 0.5) * 3, y: (Math.random() - 0.5) * 3 };
      default:
        return { x: 0, y: 0 };
    }
  }

  private getStyleFalloff(normalizedDist: number): number {
    switch (this.settings.style) {
      case "dab":
        return normalizedDist < 0.5 ? 1 : 0;
      case "tight-short":
      case "tight-medium":
      case "tight-long":
      case "tight-curl":
        return Math.max(0, 1 - normalizedDist * normalizedDist);
      case "loose-medium":
      case "loose-long":
      case "loose-curl":
        return Math.max(0, 1 - normalizedDist);
      default:
        return Math.max(0, 1 - normalizedDist);
    }
  }

  private extractRegion(full: Uint8ClampedArray, fullWidth: number, bbox: BBox): Uint8ClampedArray {
    const out = new Uint8ClampedArray(bbox.w * bbox.h * 4);
    for (let row = 0; row < bbox.h; row++) {
      const srcOff = ((bbox.y + row) * fullWidth + bbox.x) * 4;
      const dstOff = row * bbox.w * 4;
      out.set(full.subarray(srcOff, srcOff + bbox.w * 4), dstOff);
    }
    return out;
  }
}
